import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { InnerSearch } from "../components/search/InnerSearch";
import { RealtedVideo } from "../components/realtedVideo/RealtedVideo";
import { HelpfulFeedback } from "../components/HelpfulFeedback/HelpfulFeedback";

const VZY_HOME = "https://www.vzy.one/en/home";

const getYouTubeVideoId = (url = "") => {
  if (!url) return null;
  try {
    if (url.includes("youtube.com/watch")) return new URL(url).searchParams.get("v");
    if (url.includes("youtu.be")) return url.split("/").pop().split("?")[0];
  } catch {}
  return null;
};

const getEmbedUrl = (url = "") => {
  if (!url) return "";
  try {
    if (url.includes("youtube.com/watch")) {
      const videoId = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    }
    if (url.includes("youtu.be")) {
      const videoId = url.split("/").pop().split("?")[0];
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    }
    return url;
  } catch {
    return "";
  }
};

const PauseDialog = ({ onStillNeedHelp, onGoToVZY }) => (
  <div className="vzy-pause-dialog-overlay">
    <div className="vzy-pause-dialog">
      <h3 className="vzy-pause-dialog-title">Need More Help?</h3>
      <p className="vzy-pause-dialog-body">
        Do you still need help, or would you like to go to the VZY platform?
      </p>
      <div className="vzy-pause-dialog-actions">
        <button className="vzy-pause-btn-secondary" onClick={onStillNeedHelp}>
          Still Need Help
        </button>
        <button className="vzy-pause-btn-primary" onClick={onGoToVZY}>
          Go to VZY Platform
        </button>
      </div>
    </div>
  </div>
);

export const VideosDetails = () => {
  const apiBaseURL = process.env.REACT_APP_API_URL || window.location.origin;
  const { state } = useLocation();
  const navigate = useNavigate();
  const fetchIDsUsed = state?.detailsIDs;
  const [contentSteps, setContentSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const playerRef = useRef(null);
  const ytPlayerDivId = "vzy-yt-player";

  const fetchContentSteps = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        ContentCode: fetchIDsUsed?.contentCode || "",
        ContentTypeID: fetchIDsUsed?.contentTypeID || 2,
        CategoryID: fetchIDsUsed?.categoryID || 0,
        IsActive: 1,
        PageNumber: 1,
        PageSize: 10,
        SearchValue: fetchIDsUsed?.contentCode || "",
        IsSearch: fetchIDsUsed?.IsSearch || 0,
      };
      const response = await axios.post(
        `${apiBaseURL}/api/VZYHelpSupport/FetchContentSteps`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      const result = response?.data?.result;
      if (!result || (Array.isArray(result) && result.length === 0)) {
        navigate("/VZYSupport/NoResultsFound");
        return;
      }
      setContentSteps(response?.data?.result?.contentSteps ?? []);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError("Failed to load video details");
    } finally {
      setLoading(false);
    }
  }, [apiBaseURL, fetchIDsUsed?.contentCode]);

  useEffect(() => {
    fetchContentSteps();
  }, [fetchContentSteps]);

  const contentItem = contentSteps[0];
  const videoId = contentItem ? getYouTubeVideoId(contentItem.imageAndVideoUrl) : null;

  // YouTube IFrame Player API — fires dialog on pause
  useEffect(() => {
    if (!videoId) return;

    const initPlayer = () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      playerRef.current = new window.YT.Player(ytPlayerDivId, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (event) => {
            if (
              event.data === window.YT.PlayerState.PAUSED ||
              event.data === window.YT.PlayerState.ENDED
            ) {
              setShowPauseDialog(true);
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      if (!document.getElementById("yt-iframe-api-script")) {
        const tag = document.createElement("script");
        tag.id = "yt-iframe-api-script";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoId]);

  const innerSearch = {
    pageName: "Video Tutorials",
    breadcrumb: [
      { label: "Home", url: "/VZYSupport/helpSupport" },
      { label: contentItem?.contentTitle || "Video Details" },
    ],
  };

  return (
    <>
      <InnerSearch innerSearch={innerSearch} />

      {showPauseDialog && (
        <PauseDialog
          onStillNeedHelp={() => setShowPauseDialog(false)}
          onGoToVZY={() => { window.location.href = VZY_HOME; }}
        />
      )}

      <section className="inner-page-details">
        <div className="container">
          <div className="row">
            <div className="col-md-9">
              {loading && <p>Loading video...</p>}
              {error && <p className="error-text">{error}</p>}

              {!loading && !error && contentItem && (
                <>
                  <h4 className="detail-page-title">{contentItem.contentTitle}</h4>
                  <div className="faq-desciption-row">
                    {contentItem.imageAndVideoUrl && (
                      videoId
                        ? <div id={ytPlayerDivId} />
                        : (
                          <iframe
                            src={getEmbedUrl(contentItem.imageAndVideoUrl)}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={contentItem.contentTitle}
                          />
                        )
                    )}
                  </div>
                  <HelpfulFeedback
                    title="Was this video helpful?"
                    fetchIDsUsed={contentItem}
                  />
                </>
              )}
            </div>
            <div className="col-md-3">
              <h4 className="detail-page-sidebar-title">Related Videos</h4>
              <div>
                <RealtedVideo contentItem={2} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
